import type {
  CreateVendorErrorCodeRequestDto,
  ListVendorErrorCodesResponseDto,
  ReopenVendorErrorCodeRequestDto,
  UpdateVendorErrorCodeRequestDto,
  VendorErrorCodeResponseDto,
} from '@evimo/client/models';
import { ResponseError } from '@evimo/client/runtime';
import { VendorErrorCodeApiError } from '~/app/services/errorManagement/vendor-error-code-api-error';
import {
  vendorErrorCodesApi,
  vendorErrorCodesRequestInit,
} from '~/app/services/errorManagement/vendor-error-code-api-instances';

export { VendorErrorCodeApiError };

async function invokeVendorErrorCodeClient<T>(
  action: () => Promise<T>,
): Promise<T> {
  try {
    return await action();
  } catch (error) {
    if (error instanceof ResponseError) {
      throw new VendorErrorCodeApiError(error.response.status, error.message);
    }
    throw error;
  }
}

export type ListVendorErrorCodesParams = {
  page: number;
  limit: number;
  q?: string;
  manufacturerId?: string;
  sortOrder?: 'asc' | 'desc';
};

export const VendorErrorCodeService = {
  list(
    params: ListVendorErrorCodesParams,
  ): Promise<ListVendorErrorCodesResponseDto> {
    return invokeVendorErrorCodeClient(() =>
      vendorErrorCodesApi.vendorErrorCodeControllerFindAll(
        {
          page: params.page,
          limit: params.limit,
          ...(params.q?.trim() ? { q: params.q.trim() } : {}),
          ...(params.manufacturerId
            ? { manufacturerId: params.manufacturerId }
            : {}),
          ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
        },
        vendorErrorCodesRequestInit,
      ),
    );
  },

  getById(id: string): Promise<VendorErrorCodeResponseDto> {
    return invokeVendorErrorCodeClient(() =>
      vendorErrorCodesApi.vendorErrorCodeControllerFindById(
        { id },
        vendorErrorCodesRequestInit,
      ),
    );
  },

  create(
    dto: CreateVendorErrorCodeRequestDto,
  ): Promise<VendorErrorCodeResponseDto> {
    return invokeVendorErrorCodeClient(() =>
      vendorErrorCodesApi.vendorErrorCodeControllerCreate(
        { createVendorErrorCodeRequestDto: dto },
        vendorErrorCodesRequestInit,
      ),
    );
  },

  update(
    id: string,
    dto: UpdateVendorErrorCodeRequestDto,
  ): Promise<VendorErrorCodeResponseDto> {
    return invokeVendorErrorCodeClient(() =>
      vendorErrorCodesApi.vendorErrorCodeControllerUpdate(
        { id, updateVendorErrorCodeRequestDto: dto },
        vendorErrorCodesRequestInit,
      ),
    );
  },

  delete(id: string): Promise<void> {
    return invokeVendorErrorCodeClient(() =>
      vendorErrorCodesApi.vendorErrorCodeControllerDelete(
        { id },
        vendorErrorCodesRequestInit,
      ),
    );
  },

  reopen(id: string, dto: ReopenVendorErrorCodeRequestDto): Promise<void> {
    return invokeVendorErrorCodeClient(() =>
      vendorErrorCodesApi.vendorErrorCodeControllerReopen(
        { id, reopenVendorErrorCodeRequestDto: dto },
        vendorErrorCodesRequestInit,
      ),
    );
  },
};
